// @scripts
import { summaryType } from './helpers';
import {
    handleOnCellFocus,
    handleOnCellKeyDown,
    handleOnCellKeyUp,
    handleOnCheckItemChanged,
    handleOnColumnMoving,
    handleOnColumnSorting,
    handleOnCommit,
    handleOnDataFiltered,
    handleOnDataFiltering,
    handleOnEditCellEnded,
    handleOnEditCellStarting,
    handleOnTooltipShowing,
    handleOnColumnSorted
} from './events';

const checkBox = {
    activation: false,
    multipleSelection: true,
    name: 'Selection',
    rowSelectionChanging(evt) {
        evt.preventDefault();
    }
};

const filter = ({ columns, onDataFiltered, onDataFiltering }) => ({
    columnSettings: columns.map(({ allowFiltering = true, key: columnKey }) => ({
        allowFiltering,
        columnKey
    })),
    dataFiltered: () => handleOnDataFiltered({ onDataFiltered }),
    dataFiltering: (_event, ui) => handleOnDataFiltering({ columns, onDataFiltering, ui }),
    name: 'Filtering',
    persist: true,
    type: 'local'
});

const fixColumn = (columns) => ({
    columnSettings: columns.map(({ allowFixing = true, isFixed = false, key: columnKey }) => ({
        allowFixing,
        columnKey,
        isFixed
    })),
    fixingDirection: 'left',
    name: 'ColumnFixing'
});

const group = (groupByKey) => ({
    columnSettings: [{
        columnKey: groupByKey,
        isGroupBy: true
    }],
    groupByAreaVisibility: 'hidden',
    name: 'GroupBy'
});

const hiding = (columns) => ({
    columnSettings: columns.map(({ allowHiding = true, hidden = false, key: columnKey }) => ({
        allowHiding,
        columnKey,
        hidden
    })),
    name: 'Hiding'
});

const mergeCells = ({ columns, isMerged }) => ({
    columnSettings: columns.map(({ allowMerge = true, key: columnKey }) => ({ allowMerge, columnKey })),
    initialState: isMerged ? 'merged' : 'regular',
    name: 'CellMerging'
});

const moveColumn = ({ columns, onColumnMoving }) => ({
    columnMoving: () => handleOnColumnMoving({ onColumnMoving }),
    columnMovingDialogContainment: 'window',
    columnSettings: columns.map(({ allowMoving = true, key: columnKey }) => ({ allowMoving, columnKey })),
    name: 'ColumnMoving'
});

const multiColumHeader = {
    name: 'MultiColumnHeaders'
};

const paging = {
    name: 'Paging',
    type: 'local',
    pageSize: 10
};

const resize = (columns) => ({
    columnSettings: columns.map(({ allowResizing = true, key: columnKey, minimumWidth = 10 }) => ({
        allowResizing,
        columnKey,
        minimumWidth
    })),
    name: 'Resizing'
});

const rowSelectors = ({
    dataSource,
    enableCheckBox,
    enableRowIndex,
    onCheckItemChanged,
    primaryKey
}) => ({
    checkBoxStateChanged: (_event, ui) => handleOnCheckItemChanged({
        dataSource,
        onCheckItemChanged,
        primaryKey,
        ui
    }),
    enableCheckBoxes: enableCheckBox,
    enableRowNumbering: enableRowIndex,
    enableSelectAllForPaging: enableCheckBox,
    name: 'RowSelectors',
    requireSelection: enableCheckBox
});

const sort = ({ columns, onColumnSorting }) => ({
    columnSettings: columns.map(({ allowSorting = true, key: columnKey }) => ({ allowSorting, columnKey })),
    columnSorting: () => handleOnColumnSorting({ onColumnSorting }),
    columnSorted: (_event, ui) => handleOnColumnSorted({ ui }),
    mode: 'multi',
    name: 'Sorting',
    persist: true,
    type: 'local'
});

const summary = (columns) => {
    const columnSettings = columns.map(({ allowSummaries = true, key: columnKey, summary }) => {
        const { type = summaryType.SUM, calculator: summaryCalculator } = summary || {};
        const customSummary = type === summaryType.CUSTOM ? { summaryCalculator } : {};
        return ({
            allowSummaries,
            columnKey,
            summaryOperands: [{ type, ...customSummary }]
        });
    });
    return ({
        columnSettings,
        compactRenderingMode: true,
        name: 'Summaries',
        resultTemplate: '{1}',
        showDropDownButton: false,
        type: 'local'
    });
};

const tooltip = ({ columns, onTooltipShowing }) => ({
    columnSettings: columns.map(({ allowTooltips = true, key: columnKey }) => ({ allowTooltips, columnKey })),
    fadeTimespan: 100,
    hideDelay: 0,
    name: 'Tooltips',
    style: 'popover',
    tooltipShowing: (_event, ui) => handleOnTooltipShowing({ onTooltipShowing, ui }),
    visibility: 'always'
});

const update = ({
    columns,
    enableHotkeys,
    getItemById,
    getItemIndexById,
    jQueryGrid,
    onEditCellEnded,
    onEditCellStarting,
    primaryKey
}) => {
    const getEditorType = (column) => {
        const { dataSource, dataType } = column;
        let editorType;
        if (enableHotkeys && dataSource) {
            editorType = 'combo';
        } else if (dataType === 'date') {
            editorType = 'datepicker';
        }
        return editorType;
    };
    return ({
        autoCommit: true,
        columnSettings: columns.map((column) => ({
            columnKey: column.key,
            editorOptions: enableHotkeys && ({
                blur: () => handleOnCommit({
                    columns,
                    jQueryGrid,
                    onEditCellEnded,
                    primaryKey
                }),
                dataMode: column.dataMode,
                dataSource: column.dataSource,
                dropDownOpened: () => {},
                dropDownOpening: () => {},
                excludeKeys: column.excludeKeys,
                focus: handleOnCellFocus,
                headerTemplate: column.headerTemplate,
                itemTemplate: column.itemTemplate,
                keydown: handleOnCellKeyDown,
                keyup: handleOnCellKeyUp,
                maxDecimals: column.maxDecimals,
                maxLength: column.maxLength,
                maxValue: column.maxValue,
                minDecimals: column.minDecimals,
                minValue: column.minValue,
                mode: column.dataSource && 'dropdown',
                readOnly: column.readOnly,
                selectionChanged: () => {},
                selectionChanging: () => {},
                selectionOnFocus: column.selectionOnFocus,
                textAlign: column.textAlign,
                textKey: column.textKey,
                validatorOptions: column.validatorOptions,
                valueChanging: column.valueChanging,
                valueKey: column.valueKey,
                width: column.width
            }),
            editorType: getEditorType(column)
        })),
        editCellEnded: (_event, row) => handleOnEditCellEnded({ onEditCellEnded, row }, getItemIndexById(row.rowID)),
        editCellStarting: (event, ui) => handleOnEditCellStarting({
            event,
            getItemById,
            onEditCellStarting,
            ui
        }),
        editMode: 'cell',
        enableAddRow: false,
        enableDeleteRow: false,
        excelNavigationMode: false,
        horizontalMoveOnEnter: true,
        name: 'Updating',
        startEditTriggers: 'click'
    });
};

export const getFeatures = (featureProps, options, events) => {
    const {
        enableCheckBox,
        enableFilter,
        enableFixColumn,
        enableGroup,
        enableHiding,
        enableHotkeys,
        enableMergeCells,
        enableMoveColumn,
        enableMultiColumHeader,
        enablePaging,
        enableResize,
        enableRowIndex,
        enableSort,
        enableSummary,
        enableTooltip,
        enableUpdate
    } = featureProps;

    const {
        columns,
        dataSource,
        getItemById,
        getItemIndexById,
        groupByKey,
        isMerged,
        jQueryGrid,
        primaryKey
    } = options;

    const {
        onCheckItemChanged,
        onColumnMoving,
        onColumnSorting,
        onDataFiltered,
        onDataFiltering,
        onEditCellEnded,
        onEditCellStarting,
        onTooltipShowing
    } = events;

    const get = (condition, item) => (condition ? [item] : []);
    return ([
        ...get(enableCheckBox, checkBox),
        ...get(enableFilter, filter({ columns, onDataFiltered, onDataFiltering })),
        ...get(enableFixColumn, fixColumn(columns)),
        ...get(enableGroup, group(groupByKey)),
        ...get(enableHiding, hiding(columns)),
        ...get(enableMergeCells, mergeCells({ columns, isMerged })),
        ...get(enableMoveColumn, moveColumn({ columns, onColumnMoving })),
        ...get(enableMultiColumHeader, multiColumHeader),
        ...get(enablePaging, paging),
        ...get(enableResize, resize(columns)),
        ...get(enableRowIndex || enableCheckBox, rowSelectors({
            dataSource,
            enableCheckBox,
            enableRowIndex,
            onCheckItemChanged,
            primaryKey
        })),
        ...get(enableSort, sort({ columns, onColumnSorting })),
        ...get(enableSummary, summary(columns)),
        ...get(enableTooltip, tooltip({ columns, onTooltipShowing })),
        ...get(enableUpdate, update({
            columns,
            enableHotkeys,
            getItemById,
            getItemIndexById,
            jQueryGrid,
            onEditCellEnded,
            onEditCellStarting,
            primaryKey
        }))
    ]);
};
